import { QueryList } from '@angular/core';

import { SearchItem } from '../../core';
import {
    Cell,
    CellNode,
    Column,
    Context,
    DivRendererOptions,
    GridContent,
    Row,
    RowNode,
    SelectTemplateFn,
    Sort,
    SortOrder,
    sortOrderWithDefault,
} from '../../grid';
import { tableColumns } from '../fixture/table-columns';
import { ColumnId } from '../model/column-id.model';
import { SelectedFilterOption } from '../model/filter-criteria-item.model';
import { FilterOption } from '../model/filter-option.model';
import { TableData } from '../model/table-data.model';
import { TemplateId } from '../model/template-id.model';

import { createFilterOpts } from './filter-rows.function';

function selectTpl(): SelectTemplateFn {
    return (
        context: Context,
        contents: QueryList<GridContent>,
    ): GridContent => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find((e) => {
                if (e.type !== 'row') {
                    return false;
                }
                let templateId = context.ctx?.templateId;
                if (templateId == null) {
                    templateId =
                        context.r?.cells?.children?.[0].def?.ctx?.templateId ??
                        'header';
                }
                return e.id === templateId;
            });
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };
}

function compareFn(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    return rowA.def.ctx.index - rowB.def.ctx.index;
}

// function injectLines(lines: Array<string>, el: HTMLElement): void {
//     lines.forEach((line, li) => {
//         el.appendChild(document.createTextNode(line));
//         if (li !== lines.length - 1) {
//             el.appendChild(document.createElement('br'));
//         }
//     });
// }

/* TODO revise. copied from chart util */
// const MAX_LENGTH = 12;

// function _wrapText(text: string, maxLines: number, maxLength: number = MAX_LENGTH, concatLast: boolean = true): Array<string> {
//     const words = text.split(' ');
//     let lines = [];
//     let lineLength = 0;
//     words.forEach((value, index) => {
//         if (value.trim().length === 0) return;
//         lines.push(value);
//         lineLength += value.length;
//         if (index === words.length - 1) return;
//         if (lineLength >= maxLength || (index + 1 <= words.length && lineLength + words[index + 1].length >= maxLength)) {
//             lines.push('<br>');
//             lineLength = 0;
//         } else {
//             lines.push(' ');
//             lineLength++;
//         }
//     });
//     lines = lines.join('').split('<br>');
//     if (!concatLast) {
//         lines = lines.slice(0, maxLines);
//     } else {
//         if (lines.length > maxLines) {
//             const other = lines.slice(maxLines);
//             lines[maxLines - 1] += ` ${other.join(' ')}`;
//             lines = lines.slice(0, maxLines);
//         }
//     }
//     return lines;
// }

// function wrapText(el: HTMLElement): void {
//     const textEl = el.querySelector('.cmps-mi2-table--row--text') as HTMLElement;
//     if (textEl != null) {
//         const text = textEl.innerHTML.replace(/<br>/, ' ');
//         const lines = _wrapText(text, 2, text.length / 2);
//         textEl.textContent = '';
//         injectLines(lines, textEl);
//     }
// }

// function unwrapText(el: HTMLElement): void {
//     const textEl = el.querySelector('.cmps-mi2-table--row--text') as HTMLElement;
//     if (textEl != null) {
//         const text = textEl.innerHTML.replace(/<br>/, ' ');
//         textEl.textContent = '';
//         textEl.appendChild(document.createTextNode(text));
//     }
// }

// const excludeMeasure: Array<string> = [];

function willMeasure(node: CellNode): void {
    if (node.renderContext?.element != null) {
        node.renderContext.element.classList.add(
            'cmps-eval-landing-table--measure',
        );
        // if (node.context?.r?.context?.ctx?.templateId !== TemplateId.CONTENT || !excludeMeasure.includes(node.context?.c?.id)) {
        //     wrapText(node.renderContext.element);
        // }
    }
}

function didMeasure(node: CellNode): void {
    if (node.renderContext?.element != null) {
        node.renderContext.element.classList.remove(
            'cmps-eval-landing-table--measure',
        );
        // if (node.context?.r?.context?.ctx?.templateId !== TemplateId.CONTENT || !excludeMeasure.includes(node.context?.c?.id)) {
        //     unwrapText(node.renderContext.element);
        // }
    }
}

export function createTableData(
    data: SearchItem[],
    sortOptions: Sort,
): TableData {
    const columns: Column[] = tableColumns.map<Column>((e) => {
        const val: Column = {
            id: e.id,
            width: 80,
            compareFn,
            willMeasure,
            didMeasure,
        };
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const toggleFilterFilterOpts = (opts?: SelectedFilterOption[]) => {
            val.ctx.filteredFilterOpts.forEach((opt: FilterOption) => {
                opt.isSelected = opts?.some(
                    (sOpt) =>
                        sOpt.columnId === e.id && sOpt.value === opt.value,
                );
            });
            val.ctx.isFilterActive = val.ctx.filteredFilterOpts.some(
                (opt) => opt.isSelected === true,
            );
        };
        const filterOpts = createFilterOpts(data, e.id);
        val.ctx = {
            templateId: TemplateId.HEADER,
            data: e,
            filterOpts,
            filteredFilterOpts: [...filterOpts],
            isFilterActive: false,
            filterFilterOpts: (str: string, opts?: SelectedFilterOption[]) => {
                if (str == null || str.trim().length === 0) {
                    val.ctx.filteredFilterOpts = filterOpts;
                    return;
                }
                val.ctx.filteredFilterOpts = filterOpts.filter((ee) =>
                    ee.label.toLowerCase().includes(str.toLowerCase()),
                );
                toggleFilterFilterOpts(opts);
            },
            toggleFilterFilterOpts,
        };
        return val;
    });

    const rendererOpts: DivRendererOptions = {
        columns,
        defaultRenderFn: null,
        height: 900,
        horizontalScrollPadding: 9999,
        verticalScrollPadding: 9999,
    };

    const rows =
        data?.map<Row>((r, ri) => {
            const cells = columns.map<Cell>((e) => {
                return {
                    columnId: e.id,
                    height: 30,
                    ctx: {
                        templateId: TemplateId.CONTENT,
                        data: r,
                    },
                    willMeasure,
                    didMeasure,
                };
            });

            return {
                id: `${ri}`,
                height: 30,
                cells,
                ctx: {
                    templateId: TemplateId.CONTENT,
                    index: ri,
                },
            };
        }) ?? [];

    const defaultSortDef: Sort = {
        columnId: ColumnId.USER_NAME,
        order: SortOrder.ASC,
    };
    const sortDefs = sortOrderWithDefault(
        columns,
        null,
        {
            columnId: columns.find((e) => e.id === sortOptions.columnId).id,
            order: sortOptions.order,
        },
        defaultSortDef,
    );

    return {
        rendererOpts,
        selectTpl: selectTpl(),
        rows,
        sortDefs,
    };
}
